import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import TabCodeExamples from '../../../components/docs/TabCodeExamples';
import { 
  pythonAuthExample, 
  nodeAuthExample, 
  javaAuthExample, 
  goAuthExample, 
  csharpAuthExample,
  rustAuthExample,
  pythonApiExample,
  nodeApiExample,
  
} from '../../../examples/codeSnippets';
import { javaApiExample } from '../../../examples/codeSnippets/JavaApiExample';
import { csharpApiExample } from '../../../examples/codeSnippets/CSharpApiExample';
import { goApiExample } from '../../../examples/codeSnippets/GoApiExample';
// Define constants
const API_BASE_URL = "https://app.reasonshield.com/api/v1";
const API_ENDPOINT_PATH = "/lis-webhook/vendor";

// Code block component for syntax highlighting
const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'bash' }) => {
  return (
    <div className="bg-gray-900 rounded-md p-4 my-4 overflow-x-auto">
      <pre className="text-sm text-gray-300 font-mono">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const EndpointCard: React.FC<{
  method: string;
  endpoint: string;
  description: string;
  requestParams?: string;
  responseExample?: string;
}> = ({ method, endpoint, description, requestParams, responseExample }) => {
  const methodColorClass = 
    method === 'POST' ? 'bg-green-600' : 
    method === 'GET' ? 'bg-blue-600' : 
    method === 'DELETE' ? 'bg-red-600' : 
    'bg-purple-600';
  
  return (
    <div className="border border-border rounded-lg mb-8 overflow-hidden">
      <div className="flex items-center p-4 bg-background-alt border-b border-border">
        <span className={`${methodColorClass} text-white px-3 py-1 rounded-md text-sm font-semibold mr-4`}>
          {method}
        </span>
        <code className="font-mono text-secondary">{endpoint}</code>
      </div>
      <div className="p-6">
        <p className="text-text mb-4">{description}</p>
        
        {requestParams && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-2 text-primary">Request Parameters</h4>
            <CodeBlock code={requestParams} language="json" />
          </div>
        )}
        
        {responseExample && (
          <div>
            <h4 className="text-lg font-semibold mb-2 text-primary">Response Example</h4>
            <CodeBlock code={responseExample} language="json" />
          </div>
        )}
      </div>
    </div>
  );
};

const ApiDocumentationPage: React.FC = () => {
  return (
    <div className="container-custom py-16">
      <Helmet>
        <title>ReasonShield API Documentation | Vendor Integration</title>
        <meta 
          name="description" 
          content="Comprehensive API documentation for ReasonShield vendor integration. Learn how to submit data, check processing status, and retrieve reports." 
        />
      </Helmet>
      
      <h1 className="text-4xl md:text-5xl font-bold mb-4">ReasonShield API Documentation</h1>
      <p className="text-xl mb-8 text-text">
        Complete reference for integrating with the ReasonShield Vendor API.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
        <div className="md:col-span-1 space-y-6">
          <div className="bg-background-alt p-6 rounded-lg sticky top-24">
            <h3 className="text-lg font-semibold mb-4 text-primary">Contents</h3>
            <nav>
              <ul className="space-y-2">
                <li>
                  <a href="#authentication" className="text-text hover:text-primary block py-1">Authentication</a>
                </li>
                <li>
                  <a href="#endpoints" className="text-text hover:text-primary block py-1">API Endpoints</a>
                  <ul className="ml-4 space-y-1 mt-1">
                    <li>
                      <a href="#submit-data" className="text-text hover:text-primary block py-1 text-sm">Submit Data</a>
                    </li>
                    <li>
                      <a href="#check-status" className="text-text hover:text-primary block py-1 text-sm">Check Status</a>
                    </li>
                    <li>
                      <a href="#get-report" className="text-text hover:text-primary block py-1 text-sm">Get Report</a>
                    </li>
                    <li>
                      <a href="#pdf-report" className="text-text hover:text-primary block py-1 text-sm">PDF Report</a>
                    </li>
                  </ul>
                </li>
                <li>
                  <a href="#data-format" className="text-text hover:text-primary block py-1">Data Format</a>
                </li>
                <li>
                  <a href="#error-handling" className="text-text hover:text-primary block py-1">Error Handling</a>
                </li>
                <li>
                  <a href="#rate-limits" className="text-text hover:text-primary block py-1">Rate Limits</a>
                </li>
                <li>
                  <a href="#examples" className="text-text hover:text-primary block py-1">Code Examples</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        
        <div className="md:col-span-3">
          <section id="authentication" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Authentication</h2>
            <div className="p-6 rounded-lg border border-primary border-opacity-30 bg-background-alt bg-opacity-5 mb-6">
              <h3 className="text-xl font-bold mb-4 text-primary">Dual Authentication System</h3>
              <p className="mb-4">
                ReasonShield implements a dual authentication approach that requires <strong>both</strong> API key and signature verification for all interactions. This comprehensive security approach ensures protection of sensitive health data while maintaining a straightforward integration process.
              </p>
              <p className="mb-4">
                You must implement both authentication methods for every API request, regardless of endpoint or operation:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>API Key Authentication</strong>: Identifies your vendor account (authentication)</li>
                <li><strong>Request Signature Verification</strong>: Ensures data integrity and prevents tampering (verification)</li>
              </ul>
            </div>

            <div className="p-6 rounded-lg border border-border bg-background-alt bg-opacity-5 mb-6">
              <h3 className="text-xl font-bold mb-4">Benefits of Dual Authentication</h3>
              <p className="mb-4">
                Our dual authentication approach provides several key security benefits:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border border-border">
                  <h4 className="font-bold mb-2">Protection Against Credential Leakage</h4>
                  <p>Even if an API key is compromised, attackers cannot successfully make requests without the webhook secret to generate valid signatures.</p>
                </div>
                <div className="p-4 rounded-lg border border-border">
                  <h4 className="font-bold mb-2">Request Tampering Prevention</h4>
                  <p>The signature verification ensures the integrity of the data, preventing man-in-the-middle attacks and unauthorized request modification.</p>
                </div>
                <div className="p-4 rounded-lg border border-border">
                  <h4 className="font-bold mb-2">Replay Attack Protection</h4>
                  <p>Including timestamps and request IDs in signed payloads helps prevent replay attacks by ensuring each request is unique.</p>
                </div>
                <div className="p-4 rounded-lg border border-border">
                  <h4 className="font-bold mb-2">Non-repudiation</h4>
                  <p>Cryptographic signatures provide proof that requests genuinely came from your system, creating an audit trail for data submitted.</p>
                </div>
              </div>
            </div>
            
            <TabCodeExamples
              title="Authentication Implementation"
              description="Here's how to implement our dual authentication system in different languages:"
              tabs={[
                {
                  title: "Python Example",
                  language: "python",
                  code: pythonAuthExample,
                },
                {
                  title: "Node.js Example",
                  language: "javascript",
                  code: nodeAuthExample,
                },
                {
                  title: "Java Example",
                  language: "java",
                  code: javaAuthExample,
                },
                {
                  title: "Go Example",
                  language: "go",
                  code: goAuthExample,
                },
                {
                  title: "C# Example",
                  language: "csharp",
                  code: csharpAuthExample,
                },
                {
                  title: "Rust Example",
                  language: "rust",
                  code: rustAuthExample,
                }
              ]}
            />
            
            <div className="p-6 rounded-lg border border-border bg-background-alt bg-opacity-5 mt-6">
              <h3 className="text-xl font-bold mb-4">Security Implementation Guide</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold">Securely Store Credentials</h4>
                  <p>Never hardcode your API key or webhook secret in your codebase. Store them securely in environment variables or a secure key management system.</p>
                </div>
                <div>
                  <h4 className="font-bold">Implement Both Authentication Components</h4>
                  <p>Always implement both API key authentication and request signing. Neither is optional - both must be present for successful API calls.</p>
                </div>
                <div>
                  <h4 className="font-bold">Use HTTPS For All Requests</h4>
                  <p>Always use HTTPS to protect data in transit. Never send API requests over unencrypted HTTP.</p>
                </div>
                <div>
                  <h4 className="font-bold">Consistent JSON Serialization</h4>
                  <p>When generating signatures, ensure your JSON serialization produces consistent output (sorted keys, no extra whitespace). Different serialization formats will produce different signatures.</p>
                </div>
                <div>
                  <h4 className="font-bold">Implement Proper Error Handling</h4>
                  <p>Include appropriate try/catch blocks in your code to handle authentication failures and network errors gracefully.</p>
                </div>
              </div>
            </div>
          </section>
          
          <section id="endpoints" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">API Endpoints</h2>
            
            <div id="submit-data" className="mb-8">
              <EndpointCard 
                method="POST"
                endpoint={`${API_BASE_URL}${API_ENDPOINT_PATH}/submit-data`}
                description="Submit test data to the ReasonShield vendor API for processing."
                requestParams={`{
  "vendor_id": "your-vendor-id",
  "timestamp": "2024-11-10T15:30:00Z",
  "request_id": "unique-request-id",
  "test_data": {
    "test_id": "159809",
    "panel_type": "RESPIRATORY",
    "panel_size": 56,
    "patient_info": {
      "patient_id": "56012",
      "patient_name": "Mcdaniel, Garrick",
      "patient_dob": "1995-01-06",
      "patient_gender": "Male",
      "patient_age": "29 years"
    },
    "collection_info": {
      "collection_site": "NASO",
      "specimen_type": "Nasopharyngeal swab",
      "collection_date": "2024-09-10T13:11:00-06:00",
      "received_date": "2024-09-11T10:55:00-06:00"
    },
    "provider_info": {
      "provider_name": "Patel, Satish",
      "provider_location": "Main Clinic"
    },
    "test_details": {
      "viruses": [
        {
          "name": "SARS-CoV-2 (N gene)",
          "result": "Not Detected"
        }
      ],
      "bacteria": [
        {
          "name": "Bordetella pertussis",
          "result": "Not Detected"
        }
      ],
      "resistance_genes": [
        {
          "name": "beta-lactamase CTX-M",
          "result": "Detected"
        }
      ],
      "fungi": [
        {
          "name": "Candida albicans",
          "result": "Not Detected"
        }
      ]
    },
    "metadata": {
      "report_status": "Final",
      "report_date": "2024-09-12T09:18:00-06:00",
      "performing_lab": "Main Laboratory",
      "test_method": "PCR"
    }
  }
}`}
                responseExample={`{
  "data_id": "abc123xyz456",
  "status": "ACCEPTED",
  "message": "Data received and queued for processing",
  "timestamp": "2024-11-10T15:30:05Z"
}`}
              />
            </div>
            
            <div id="check-status" className="mb-8">
              <EndpointCard 
                method="GET"
                endpoint={`${API_BASE_URL}${API_ENDPOINT_PATH}/status/{data_id}`}
                description="Check the processing status of previously submitted data."
                responseExample={`{
  "data_id": "abc123xyz456",
  "status": "COMPLETED",
  "message": "Data processing completed successfully",
  "timestamp": "2024-11-10T15:32:18Z",
  "report_available": true,
  "report_url": "/lis-webhook/vendor/report/abc123xyz456"
}`}
              />
            </div>
            
            <div id="get-report" className="mb-8">
              <EndpointCard 
                method="GET"
                endpoint={`${API_BASE_URL}${API_ENDPOINT_PATH}/report/{data_id}`}
                description="Retrieve the JSON report for processed data."
                responseExample={`{
  "data_id": "abc123xyz456",
  "report_data": {
    "test_id": "159809",
    "analysis_date": "2024-11-10T15:32:12Z",
    "risk_assessment": {
      "overall_risk": "MEDIUM",
      "risk_factors": [
        {
          "factor": "Resistance Gene Detection",
          "severity": "HIGH",
          "details": "Beta-lactamase CTX-M detected"
        }
      ]
    },
    "recommendations": [
      {
        "type": "ANTIBIOTIC",
        "recommendation": "Consider alternative non-beta-lactam antibiotics",
        "rationale": "Presence of beta-lactamase CTX-M indicates resistance to most penicillins and cephalosporins"
      }
    ],
    "pdf_report_url": "/lis-webhook/vendor/pdf-report/abc123xyz456"
  }
}`}
              />
            </div>
            
            <div id="pdf-report">
              <EndpointCard 
                method="GET"
                endpoint={`${API_BASE_URL}${API_ENDPOINT_PATH}/pdf-report/{data_id}`}
                description="Download a PDF version of the report. Returns a binary PDF file."
              />
            </div>
          </section>
          
          <section id="data-format" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Data Format</h2>
            <div className="bg-background-alt p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-4">Required Fields</h3>
              <p className="mb-4">
                The following fields are required when submitting data:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li><code className="text-secondary">vendor_id</code>: Your assigned vendor ID.</li>
                <li><code className="text-secondary">timestamp</code>: ISO-8601 formatted timestamp.</li>
                <li><code className="text-secondary">request_id</code>: A unique identifier for the request.</li>
                <li><code className="text-secondary">test_data</code>: The test data object containing all test information.</li>
                <li><code className="text-secondary">test_data.test_id</code>: Unique identifier for the test.</li>
                <li><code className="text-secondary">test_data.panel_type</code>: Type of panel (e.g., RESPIRATORY, UTI, GI).</li>
                <li><code className="text-secondary">test_data.patient_info</code>: Basic patient information.</li>
                <li><code className="text-secondary">test_data.collection_info</code>: Specimen collection details.</li>
              </ul>
            </div>
            
            <div className="bg-background-alt p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">Test Result Format</h3>
              <p className="mb-4">
                Each pathogen or gene result should follow this format:
              </p>
              <CodeBlock code={`{
  "name": "Pathogen or Gene Name",
  "result": "Detected" | "Not Detected"
}`} language="json" />
              <p className="mt-4">
                Results can be grouped into categories such as <code className="text-secondary">viruses</code>, <code className="text-secondary">bacteria</code>, <code className="text-secondary">resistance_genes</code>, and <code className="text-secondary">fungi</code>.
              </p>
            </div>
          </section>
          
          <section id="error-handling" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Error Handling</h2>
            <div className="bg-background-alt p-6 rounded-lg">
              <p className="mb-4">
                The API uses standard HTTP status codes to indicate success or failure:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li><code className="text-secondary">200 OK</code>: The request was successful.</li>
                <li><code className="text-secondary">202 Accepted</code>: The request was accepted for processing.</li>
                <li><code className="text-secondary">400 Bad Request</code>: Invalid request format or missing required fields.</li>
                <li><code className="text-secondary">401 Unauthorized</code>: Authentication failed.</li>
                <li><code className="text-secondary">403 Forbidden</code>: Insufficient permissions.</li>
                <li><code className="text-secondary">404 Not Found</code>: Resource not found.</li>
                <li><code className="text-secondary">429 Too Many Requests</code>: Rate limit exceeded.</li>
                <li><code className="text-secondary">500 Internal Server Error</code>: Server-side error.</li>
              </ul>
              <p className="mt-4">
                Error responses will include a JSON object with:
              </p>
              <CodeBlock code={`{
  "error": "error_code",
  "message": "Human-readable error message",
  "timestamp": "2024-11-10T15:30:05Z"
}`} language="json" />
            </div>
          </section>
          
          <section id="rate-limits" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Rate Limits</h2>
            <div className="bg-background-alt p-6 rounded-lg">
              <p className="mb-4">
                The API has the following rate limits:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Data submission: 100 requests per minute</li>
                <li>Status checks: 300 requests per minute</li>
                <li>Report retrieval: 60 requests per minute</li>
              </ul>
              <p className="mt-4">
                If you exceed these limits, you will receive a 429 Too Many Requests status code. The response will include a Retry-After header indicating when you can retry the request.
              </p>
            </div>
          </section>
          
          <section id="examples" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Code Examples</h2>
            
            <div className="bg-background-alt p-6 rounded-lg mb-8">
              <h3 className="text-xl font-semibold mb-4">Complete API Usage Examples</h3>
              <p className="mb-4">
                These examples demonstrate the full workflow of submitting data, checking status, and retrieving reports:
              </p>
              
              <TabCodeExamples
              
                title="API Usage Examples"
                description="Complete implementation examples in various languages:"
                tabs={[
                  {
                    title: "Python",
                    language: "python",
                    code: pythonApiExample,
                  },
                  {
                    title: "Node.js",
                    language: "javascript",
                    code: nodeApiExample,
                  },
                  {
                    title: "Java",
                    language: "java",
                    code: javaApiExample,
                  },
                  {
                    title: "C#",
                    language: "csharp",
                    code: csharpApiExample,
                  },
                  {
                    title: "Go",
                    language: "go",
                    code: goApiExample,
                  },
                  
                  
                  
                ]}
              />
            </div>
            
            <div className="text-center mt-6">
              <p className="text-primary mb-4">
                View more examples in our <Link to="/vendor-integration" className="underline hover:text-secondary">Integration Guide</Link> or on our <a href="https://github.com/reasonshield/api-examples" target="_blank" rel="noopener noreferrer" className="underline hover:text-secondary">GitHub repository</a>.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentationPage; 